(() => {
  const selectors = {
    header: '[data-site-header]',
    mobileToggle: '[data-mobile-toggle]',
    mobileMenu: '[data-mobile-menu]',
    cartDrawer: '[data-cart-drawer]',
    cartOpen: '[data-cart-open]',
    cartClose: '[data-cart-close]',
    cartCount: '[data-cart-count]',
    cartItems: '[data-cart-items]',
    cartSubtotal: '[data-cart-subtotal]',
    cartEmpty: '[data-cart-empty]',
    cartFooter: '[data-cart-footer]',
    quantityControl: '[data-quantity-control]',
    quantityInput: '[data-quantity-input]',
    productForm: '[data-product-form]',
    optionInput: '[data-option-input]',
    productJson: '[data-product-json]',
    variantId: '[data-variant-id]',
    productPrice: '[data-product-price]',
    addStatus: '[data-add-status]',
    productMedia: '[data-product-media]',
    mediaButton: '[data-media-button]',
    cartChange: '[data-cart-change]',
    faqToggle: '[data-faq-details]'
  };

  const money = (amount) => {
    const cents = typeof amount === 'number' ? amount : Number(amount || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.theme?.money?.currency || 'USD'
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const qsa = (scope, query) => Array.from(scope.querySelectorAll(query));
  const qs = (scope, query) => scope.querySelector(query);

  const themeState = { cart: null };

  const syncHeader = () => {
    const header = document.querySelector(selectors.header);
    if (!header) return;
    header.classList.toggle('is-scrolled', window.scrollY > 20);
  };

  const syncBodyCartState = (isOpen) => {
    document.body.classList.toggle('drawer-open', isOpen);
    document.body.style.overflow = isOpen ? 'hidden' : '';
  };

  const setCartOpen = (open) => {
    const drawer = document.querySelector(selectors.cartDrawer);
    if (!drawer) return;
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    syncBodyCartState(open);
  };

  const renderCartItems = (cart) => {
    const drawer = document.querySelector(selectors.cartDrawer);
    if (!drawer) return;

    const list = qs(drawer, selectors.cartItems);
    const subtotal = qs(drawer, selectors.cartSubtotal);
    const countEls = qsa(document, selectors.cartCount);
    const empty = qs(drawer, selectors.cartEmpty);
    const footer = qs(drawer, selectors.cartFooter);

    if (subtotal) subtotal.textContent = money(cart.total_price);
    countEls.forEach((el) => {
      el.textContent = String(cart.item_count || 0);
      el.hidden = !cart.item_count;
    });

    if (!list) return;

    if (!cart.items.length) {
      list.innerHTML = '';
      if (empty) empty.hidden = false;
      if (footer) footer.hidden = true;
      return;
    }

    if (empty) empty.hidden = true;
    if (footer) footer.hidden = false;

    list.innerHTML = cart.items
      .map((item) => {
        const optionText = item.options_with_values
          ?.filter((option) => option.value && option.value !== 'Default Title')
          .map((option) => `${option.name}: ${option.value}`)
          .join(' · ');

        return `
          <article class="cart-drawer__item">
            <a class="cart-drawer__image" href="${item.url}">
              ${item.image ? `<img src="${item.image}" alt="${item.product_title}">` : ''}
            </a>
            <div>
              <h3 class="cart-drawer__title"><a href="${item.url}">${item.product_title}</a></h3>
              ${optionText ? `<div class="cart-drawer__meta">${optionText}</div>` : ''}
              <div class="cart-drawer__line-footer">
                <div class="quantity-field" data-line-key="${item.key}">
                  <button type="button" data-cart-change="-1" aria-label="Decrease quantity">−</button>
                  <input type="number" value="${item.quantity}" min="1" aria-label="Quantity" readonly>
                  <button type="button" data-cart-change="1" aria-label="Increase quantity">+</button>
                </div>
                <button class="cart-link-button" type="button" data-line-key="${item.key}" data-cart-remove>Remove</button>
                <span class="cart-drawer__price">${money(item.final_line_price)}</span>
              </div>
            </div>
          </article>
        `;
      })
      .join('');
  };

  const refreshCart = async ({ openDrawer = false, reloadPage = false } = {}) => {
    const response = await fetch(`${window.theme.routes.cart}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) return;

    const cart = await response.json();
    themeState.cart = cart;
    renderCartItems(cart);

    if (openDrawer) setCartOpen(true);
    if (reloadPage && document.querySelector('[data-cart-page]')) {
      window.location.reload();
    }
  };

  const changeCartLine = async (key, quantity) => {
    const response = await fetch(window.theme.routes.cartChange, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: key, quantity })
    });

    if (!response.ok) return;
    await refreshCart({ reloadPage: true });
  };

  const updateProductVariant = (form) => {
    const jsonEl = qs(form, selectors.productJson);
    const variantInput = qs(form, selectors.variantId);
    if (!jsonEl || !variantInput) return;

    const product = JSON.parse(jsonEl.textContent);
    const selectedOptions = qsa(form, selectors.optionInput)
      .filter((input) => input.checked)
      .reduce((memo, input) => {
        memo[input.name] = input.value;
        return memo;
      }, {});

    const variant = product.variants.find((candidate) =>
      candidate.options.every((optionValue, index) => {
        const optionName = product.options[index];
        return selectedOptions[optionName] === optionValue;
      })
    );

    const scope = form.closest('[data-product-root]');
    const priceTarget = scope ? qs(scope, selectors.productPrice) : null;
    const status = scope ? qs(scope, selectors.addStatus) : null;
    const submit = qs(form, '[type="submit"]');

    if (!variant) {
      if (submit) submit.disabled = true;
      if (status) status.textContent = 'Selected combination unavailable.';
      return;
    }

    variantInput.value = variant.id;
    if (priceTarget) {
      const compare = variant.compare_at_price && variant.compare_at_price > variant.price
        ? ` <span class="text-muted">${money(variant.compare_at_price)}</span>`
        : '';
      priceTarget.innerHTML = `${money(variant.price)}${compare}`;
    }
    if (submit) submit.disabled = !variant.available;
    if (status) {
      status.textContent = variant.available ? 'Ready to add to cart.' : 'Currently unavailable.';
      status.classList.toggle('status-note--success', variant.available);
    }

    const mediaId = variant.featured_media?.id;
    if (mediaId) {
      if (scope) {
        qsa(scope, selectors.productMedia).forEach((media) => {
          media.hidden = String(media.dataset.mediaId) !== String(mediaId);
        });
        qsa(scope, selectors.mediaButton).forEach((button) => {
          button.classList.toggle('is-active', String(button.dataset.mediaId) === String(mediaId));
        });
      }
    }
  };

  const bindProductForms = () => {
    qsa(document, selectors.productForm).forEach((form) => {
      qsa(form, selectors.optionInput).forEach((input) => {
        input.addEventListener('change', () => updateProductVariant(form));
      });

      qsa(form, selectors.quantityControl).forEach((button) => {
        button.addEventListener('click', () => {
          const input = qs(form, selectors.quantityInput);
          if (!input) return;
          const delta = Number(button.dataset.quantityControl || 0);
          input.value = String(Math.max(1, Number(input.value || 1) + delta));
        });
      });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const submit = qs(form, '[type="submit"]');
        const scope = form.closest('[data-product-root]');
        const status = scope ? qs(scope, selectors.addStatus) : null;
        const formData = new FormData(form);

        if (submit) submit.disabled = true;
        if (status) {
          status.textContent = 'Adding to cart...';
          status.classList.remove('status-note--success');
        }

        const response = await fetch(window.theme.routes.cartAdd, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          body: formData
        });

        if (!response.ok) {
          if (status) status.textContent = 'Unable to add item. Please check your selection.';
          if (submit) submit.disabled = false;
          return;
        }

        await refreshCart({ openDrawer: true });
        if (status) {
          status.textContent = 'Added to cart.';
          status.classList.add('status-note--success');
        }
        if (submit) submit.disabled = false;
      });

      updateProductVariant(form);
    });
  };

  const bindMediaButtons = () => {
    qsa(document, selectors.mediaButton).forEach((button) => {
      button.addEventListener('click', () => {
        const mediaId = button.dataset.mediaId;
        const scope = button.closest('[data-product-root]') || document;
        qsa(scope, selectors.productMedia).forEach((media) => {
          media.hidden = String(media.dataset.mediaId) !== String(mediaId);
        });
        qsa(scope, selectors.mediaButton).forEach((other) => {
          other.classList.toggle('is-active', other === button);
        });
      });
    });
  };

  const bindCartActions = () => {
    document.addEventListener('click', async (event) => {
      const openButton = event.target.closest(selectors.cartOpen);
      const closeButton = event.target.closest(selectors.cartClose);
      const mobileToggle = event.target.closest(selectors.mobileToggle);
      const quantityChange = event.target.closest(selectors.cartChange);
      const removeButton = event.target.closest('[data-cart-remove]');

      if (openButton) {
        event.preventDefault();
        setCartOpen(true);
        if (!themeState.cart) await refreshCart();
      }

      if (closeButton) {
        event.preventDefault();
        setCartOpen(false);
      }

      if (mobileToggle) {
        const menu = document.querySelector(selectors.mobileMenu);
        if (!menu) return;
        const expanded = mobileToggle.getAttribute('aria-expanded') === 'true';
        mobileToggle.setAttribute('aria-expanded', String(!expanded));
        menu.hidden = expanded;
        const header = document.querySelector(selectors.header);
        if (header) header.dataset.open = String(!expanded);
      }

      if (quantityChange) {
        event.preventDefault();
        const wrapper = quantityChange.closest('[data-line-key]');
        const input = wrapper?.querySelector('input');
        if (!wrapper || !input) return;
        const nextQuantity = Math.max(0, Number(input.value || 1) + Number(quantityChange.dataset.cartChange || 0));
        await changeCartLine(wrapper.dataset.lineKey, nextQuantity);
      }

      if (removeButton) {
        event.preventDefault();
        await changeCartLine(removeButton.dataset.lineKey, 0);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setCartOpen(false);
    });
  };

  const bindFaq = () => {
    qsa(document, selectors.faqToggle).forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        qsa(document, selectors.faqToggle).forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
      });
    });
  };

  document.addEventListener('DOMContentLoaded', async () => {
    syncHeader();
    window.addEventListener('scroll', syncHeader, { passive: true });
    bindCartActions();
    bindProductForms();
    bindMediaButtons();
    bindFaq();
    await refreshCart();
  });
})();
